import React from "react";
import styled from "styled-components";

export const productOptionsDelta = (options, picks) => (options || []).reduce((sum, option) => {
  const choice = (option.choices || []).find((candidate) => candidate.value === picks?.[option.fieldKey]);
  return sum + (Number(choice?.priceDelta) || 0);
}, 0);

export const missingProductChoice = (options, picks) => {
  const missing = (options || []).find((option) => option.required &&
    !(option.choices || []).some((choice) => choice.value === picks?.[option.fieldKey]));
  return missing ? missing.label || missing.fieldKey : null;
};

// Full selling prices stay visible beside the choices, on the product page.
export default function BrandProductOptions({ options, selectedSpecs, onChange, basePrice, format }) {
  if (!options.length) return null;
  return (
    <Section aria-label="Product options">
      <h2>Choose size or options</h2>
      {options.map((option) => {
        const label = option.label || option.fieldKey;
        const choose = (value) => {
          const next = { ...selectedSpecs };
          if (value) next[option.fieldKey] = value;
          else delete next[option.fieldKey];
          onChange(next);
        };
        const choices = [
          ...(!option.required ? [{ value: "", label: "No selection" }] : []),
          ...(option.choices || []),
        ];
        return (
          <Group key={option.fieldKey}>
            <legend>{label} <span>{option.required ? "Required" : "Optional"}</span></legend>
            <p>{option.required ? `Choose one ${label.toLowerCase()} before adding to your cart.` : "Choose one, or continue without this option."}</p>
            <Choices role="radiogroup" aria-label={label} aria-required={Boolean(option.required)}>
              {choices.map((choice) => {
                const active = (selectedSpecs[option.fieldKey] || "") === choice.value;
                const next = { ...selectedSpecs, [option.fieldKey]: choice.value };
                const price = basePrice == null ? null : Number(basePrice) + productOptionsDelta(options, next);
                return (
                  <Choice key={choice.value} $active={active}>
                    <input type="radio" name={`product-option-${option.fieldKey}`} checked={active}
                      aria-label={`${choice.label || choice.value}${price == null ? "" : `, ${format(price)}`}`}
                      onChange={() => choose(choice.value)} />
                    <span>{choice.label || choice.value}</span>
                    {price != null && <strong>{format(price)}</strong>}
                  </Choice>
                );
              })}
            </Choices>
          </Group>
        );
      })}
    </Section>
  );
}

const Section = styled.section`
  border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px; background: #f8fafc;
  h2 { font-size: 1rem; margin: 0 0 14px; color: #334155; }
`;
const Group = styled.fieldset`
  border: 0; padding: 0; margin: 0 0 16px; min-width: 0;
  &:last-child { margin-bottom: 0; }
  legend { width: auto; font-size: .95rem; font-weight: 700; margin: 0; color: #1a1a2e; }
  legend span { font-size: .75rem; font-weight: 600; background: #e2e8f0; border-radius: 999px; padding: 3px 9px; margin-left: 8px; }
  p { color: #64748b; font-size: .8rem; margin: 6px 0 10px; }
`;
const Choices = styled.div`display: flex; flex-wrap: wrap; gap: 10px;`;
const Choice = styled.label`
  flex: 1 1 150px; display: grid; grid-template-columns: 18px 1fr; gap: 5px 8px;
  padding: 12px; margin: 0; border: 2px solid ${(p) => p.$active ? "var(--brand-primary, #324d54)" : "#dbe3ea"};
  border-radius: 10px; background: ${(p) => p.$active ? "var(--brand-secondary-soft, #eef5f5)" : "#fff"}; cursor: pointer;
  input { margin: 3px 0 0; accent-color: var(--brand-primary, #324d54); }
  span { color: #334155; font-weight: 600; font-size: .88rem; }
  strong { grid-column: 2; color: var(--brand-primary, #324d54); font-size: .9rem; }
  &:focus-within { outline: 2px solid var(--brand-primary, #324d54); outline-offset: 3px; }
`;
